import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    FormControl,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useBudgetFormContext } from "../../context/BudgetFormContext";

const BudgetAccommodationSelect = ({ budget, handleSubmitModal }) => {

    const { budgets, hashBudgets, paymentTypes } = useBudgetFormContext();

    const [selectedItem, setSelectedItem] = useState(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState(null);

    const [hasCopied, setHasCopied] = useState(false);

    const infoCardStyle = {
        flex: "1 1 200px",
        backgroundColor: "#fff",
        border: "1px solid #eee",
        borderRadius: 2,
        p: "6px 12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        margin: "6px 2px"
    };

    const labelStyle = {
        textTransform: "uppercase",
        fontWeight: 500,
        letterSpacing: "0.05em",
        color: "#6b7280",
    };

    const boxSubmitButtonStyle = {
        padding: "24px",
        minWidth: "900",
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        marginTop: "30px",
    };

    const formatPrice = (price) =>
        new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(price);

    const percentageDiscountValue = useMemo(() => {
        if (!selectedItem) return 1;

        return selectedPaymentType?.percentage_discount ?? 1;
    }, [selectedPaymentType, paymentTypes, selectedItem]);

    const totalValue = selectedItem
        ? selectedItem.total_cost_accomodation * percentageDiscountValue
        : null;

    useEffect(() => {
        if (!selectedItem) {
            setSelectedPaymentType(null);
        }
    }, [selectedItem]);

    const handleEnvioOrcamento = async () => {
        if (isSubmiting) return;

        setIsSubmiting(true);

        try {
            await handleSubmitModal(
                selectedItem?.acommodation_description,
                selectedPaymentId
            );
        } finally {
            setIsSubmiting(false);
        }
    };

	const handleCopy = async () => {
		const link = `https://pay.costao.com.br/orcamentos/${hashBudgets}`;
		try {
			navigator.clipboard.writeText(link)
			.then(() => console.log("Texto copiado!"))
			.catch(err => console.error("Erro ao copiar:", err));

			setHasCopied(true);

            setTimeout(() => setHasCopied(false), 2000);
		} catch (err) {
			console.error("Falha ao copiar:", err);
		}
	};

    const periodosEspeciais = [
        // {
        //     start: new Date("2026-12-21T00:00:00"),
        //     end: new Date("2027-01-15T00:00:00"),
        // },
        // {
        //     start: new Date("2027-02-05T00:00:00"),
        //     end: new Date("2027-02-10T00:00:00"),
        // },
    ];

    const intersects = (start1, end1, start2, end2) => {
        return start1 <= end2 && end1 >= start2;
    };

    const isPeriodoEspecial = periodosEspeciais.some((periodo) =>
        intersects(
            budget.check_in,
            budget.check_out,
            periodo.start,
            periodo.end
    )
    );

    const filtrarPagamento = (payment_type) => {
        if (isPeriodoEspecial) {
            return !payment_type.refundable;
        }
        return true;
    };

    return (
        <Card>
            <Box sx={{ p: 3, minWidth: 900, backgroundColor: "#f9fafb", borderBottom: "1px solid #eee" }}>
                <Typography variant="caption" sx={labelStyle}>
                    Selecione a acomodação:
                </Typography>

                <Select
                    value={selectedItem?.acommodation_description || ""}
                    onChange={e => {
                        setSelectedItem(
                            budget.prices?.find(
                                item => item.acommodation_description === e.target.value
                            ) || null
                        )
                        if(e.target.value === "") {
                            setSelectedPaymentId(null);
                            setSelectedPaymentType(null);
                        }
                    }}
                    disabled={budgets.length > 1}
                    displayEmpty
                    sx={{ 
                        m: 1, 
                        bgColor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row' 
                    }}
                >
                    <MenuItem value="">
                        <strong>TODAS</strong>
                    </MenuItem>

                    {budget.prices?.map(item => (
                        <MenuItem
                            key={item.acommodation_description}
                            value={item.acommodation_description}
                        >
                            {item.acommodation_description}
                        </MenuItem>
                    ))}
                </Select>
                {budgets.length > 1 &&
                    <Typography variant="caption" sx={{...labelStyle, color: "#ff0000ff", fontSize: '12px'}}>
                        Escolha apenas um orçamento para escolher a acomodação       
                    </Typography>
                }
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={infoCardStyle}>
                        <Typography variant="caption" sx={labelStyle}>Descrição</Typography>
                        <Typography variant="body1">
                            {selectedItem?.acommodation_description || "-"}
                        </Typography>
                    </Box>
                    <Box sx={infoCardStyle}>
                        <Typography variant="caption" sx={labelStyle}>Valor Total</Typography>
                        <Typography variant="body1">
                            {totalValue ? formatPrice(totalValue) : "-"}
                        </Typography>
                    </Box>
                    <Box sx={infoCardStyle}>
                        <Typography variant="caption" sx={labelStyle}>Link Pagamento</Typography>
                        <div style={{display: "flex", gap: '8px'}}>
                            <Typography variant="body1">
                                {`https://pay.costao.com.br/orcamentos/${hashBudgets}`}
                            </Typography>
                            {
                                hasCopied ? 
                                <CheckIcon/>
                                :
                                <ContentCopyIcon sx={{cursor: 'pointer'}} onClick={handleCopy}/>
                            }
                        </div>
                    </Box>
                </Box>
                <Box sx={{ mt: 3, display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                        Forma de Pagamento
                    </Typography>

                    <FormControl component="fieldset" sx={{ mt: 1, ml: 5, width: "fit-content" }}>

                        <RadioGroup
                            value={selectedPaymentId ?? ""}
                            disabled={!selectedItem}
                        >
                            {Object.entries(paymentTypes)
                                .filter(([_, payment_type]) => (filtrarPagamento(payment_type)))
                                .map(([id, payment_type]) => (
                                <FormControlLabel
                                    key={id}
                                    value={String(payment_type.id)}
                                    control={<Radio />}
                                    label={payment_type.description}
                                    disabled={!selectedItem}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if(!selectedItem) return;

                                        if (String(payment_type.id) === selectedPaymentId) {
                                            setSelectedPaymentId(null);
                                            setSelectedPaymentType(null);
                                        } else {
                                            setSelectedPaymentId(String(payment_type.id));
                                            setSelectedPaymentType(payment_type);
                                        }
                                    }}
                                />
                            ))}
                        </RadioGroup>

                    </FormControl>
                </Box>
                <Box sx={boxSubmitButtonStyle}>
                    <Typography variant="caption" sx={labelStyle}>
                        Envie o link de pagamento clicando abaixo.
                    </Typography>
                    <Button
                        sx={{ textTransform: "capitalize", color: "#fff", maxWidth: '280px' }}
                        color="fifth"
                        variant="contained"
                        onClick={handleEnvioOrcamento}
                        loading={isSubmiting}
                        loadingPosition="center"
                    >
                        {isSubmiting ? 'Enviando...' : 'Enviar Orçamento'}
                    </Button>
                </Box>
            </Box>
        </Card>
    );
};

BudgetAccommodationSelect.propTypes = {
    budget: PropTypes.object.isRequired,
    handleSubmitModal: PropTypes.func.isRequired,
};

export default BudgetAccommodationSelect;